/**
 * MyScoringCard — Score personnel agent (lecture seule).
 *
 * Affiche :
 *   - Score pondéré /100 + statut (Performant / À suivre / À accompagner)
 *   - Détail par axe (poids appliqué + note + contribution)
 *   - Badge "Barème personnalisé" ou "Barème par défaut"
 *   - Sélecteur de période (mois précédent par défaut)
 *   - Bouton Lexique (modale)
 *
 * Référence métier : `scoring_agents_final_avec_prompts_360.xlsx`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Award, BookOpen, Sparkles, ShieldCheck, LineChart } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const AXIS_LABELS = {
  caisse: 'Caisse',
  ponctualite: 'Ponctualité / assiduité',
  sadaskincare: 'SADASkinCare',
  sadahealth: 'SADAHealth',
  sadarayon: 'SADARayon',
  sadagas: 'SADAGAS',
  polyvalence: 'Polyvalence',
  titulaire: 'Appréciation titulaire',
  manager_direct: 'Appréciation manager',
  resultats_equipe: 'Résultats équipe',
  perf_commerciale_perso: 'Performance commerciale',
  discipline_equipe: 'Discipline opérationnelle',
  disponibilite_stock: 'Disponibilité / stock',
};

const previousMonthPeriod = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const StatusBadge = ({ score }) => {
  if (score == null) return <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-100 text-zinc-500">Aucune saisie</span>;
  const cls = score >= 80 ? 'bg-emerald-100 text-emerald-700'
    : score >= 60 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  const label = score >= 80 ? 'Performant' : score >= 60 ? 'À suivre' : 'À accompagner';
  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${cls}`}>{label}</span>;
};

const LexiconModal = ({ open, onClose, lexicon, profile }) => {
  if (!open) return null;
  const block = lexicon?.[profile] || {};

  const downloadLexicon = async (format) => {
    try {
      const r = await axios.get(`${API}/agent-scoring/lexicon/download?format=${format}`, {
        headers: auth(),
        responseType: 'blob',
      });
      const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const blobUrl = URL.createObjectURL(new Blob([r.data], { type: mime }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `lexique_scoring_pharmacie_sada.${format}`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="agent-lexicon-modal">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-bold text-zinc-900">
            <BookOpen className="inline w-5 h-5 mr-1 text-[#D97706]" /> Comprendre mon score
          </h3>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => downloadLexicon('pdf')} className="h-8 border-red-200 text-red-700 hover:bg-red-50 text-xs" data-testid="agent-lexicon-download-pdf">
              📄 PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => downloadLexicon('docx')} className="h-8 border-blue-200 text-blue-700 hover:bg-blue-50 text-xs" data-testid="agent-lexicon-download-docx">
              📝 Word
            </Button>
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 text-xl leading-none px-2">✕</button>
          </div>
        </div>
        <div className="space-y-3">
          {Object.entries(block).map(([k, v]) => (
            <div key={k} className="border-l-4 border-[#D97706] pl-3 py-1">
              <h4 className="font-semibold text-zinc-900 text-sm">{v.label}</h4>
              <p className="text-xs text-zinc-600 mt-0.5">{v.def}</p>
              {v.ref && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 mt-1.5 text-[11px]">
                  {Object.entries(v.ref).map(([range, text]) => (
                    <div key={range} className="bg-zinc-50 rounded p-1.5">
                      <strong className="text-zinc-700">{range}</strong>
                      <p className="text-zinc-500">{text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-400 mt-4 text-center">Échelle : 0-3 insuffisant · 4-5 fragile · 6-7 correct · 8-9 bon · 10 excellent.</p>
      </div>
    </div>
  );
};

const MyScoringCard = () => {
  const [period, setPeriod] = useState(previousMonthPeriod());
  const [score, setScore] = useState(null);
  const [lexicon, setLexicon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lexOpen, setLexOpen] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, lex] = await Promise.all([
        axios.get(`${API}/agent-scoring/score/me?period=${period}`, { headers: auth() }),
        axios.get(`${API}/agent-scoring/lexicon`, { headers: auth() }),
      ]);
      setScore(s.data);
      setLexicon(lex.data);
    } catch { setScore(null); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { reload(); }, [reload]);

  const hasData = score?.score_normalized != null;
  const profile = score?.is_cyrille ? 'cyrille' : 'agent';
  const themeColor = score?.is_cyrille ? '#D97706' : '#0F5947';

  const sortedAxes = useMemo(() => {
    if (!score?.per_axis) return [];
    return Object.entries(score.per_axis).sort((a, b) => {
      const ca = typeof a?.[1]?.contribution === 'number' ? a[1].contribution : 0;
      const cb = typeof b?.[1]?.contribution === 'number' ? b[1].contribution : 0;
      return cb - ca;
    });
  }, [score]);

  return (
    <Card className="border-2 shadow-card rounded-2xl overflow-hidden" style={{ borderColor: hasData ? themeColor : '#E7E5E4' }} data-testid="my-scoring-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            {score?.is_cyrille ? (
              <ShieldCheck className="w-7 h-7 text-amber-600" />
            ) : (
              <Award className="w-7 h-7" style={{ color: themeColor }} />
            )}
            <div>
              <h3 className="font-display text-lg font-bold text-zinc-900">Mon Scoring</h3>
              <p className="text-xs text-zinc-500">
                {score?.is_cyrille ? 'Barème Manager' : score?.is_custom_weights ? 'Barème personnalisé' : 'Barème par défaut'}
                {' · '}{period}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)}
              className="h-9 px-2 rounded border border-zinc-300 text-sm" data-testid="my-scoring-period" />
            <Button variant="outline" size="sm" onClick={() => setLexOpen(true)} className="h-9" data-testid="my-scoring-lexicon">
              <BookOpen className="w-4 h-4 mr-1" /> Lexique
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-zinc-400 text-center py-6">Chargement de votre score…</p>
        ) : !hasData ? (
          <div className="text-center py-6 border-2 border-dashed border-zinc-200 rounded-lg">
            <Sparkles className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
            <p className="text-sm text-zinc-500">Aucune note enregistrée pour {period}.</p>
            <p className="text-xs text-zinc-400 mt-1">Votre score apparaîtra dès que l'administration aura saisi vos évaluations.</p>
          </div>
        ) : (
          <>
            {/* Score principal */}
            <div className="flex items-center justify-between mb-5 p-4 rounded-xl" style={{ background: `linear-gradient(135deg, ${themeColor}11, ${themeColor}22)` }}>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">Score pondéré</p>
                <p className="text-4xl font-display font-bold" style={{ color: themeColor }}>
                  {score.score_normalized.toFixed(1)}<span className="text-xl text-zinc-400">/100</span>
                </p>
                <div className="mt-1.5"><StatusBadge score={score.score_normalized} /></div>
              </div>
              <LineChart className="w-14 h-14" style={{ color: themeColor, opacity: 0.25 }} />
            </div>

            {/* Détail par axe */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-zinc-600 uppercase tracking-wider mb-2">Détail par axe</h4>
              {sortedAxes.map(([axe, data]) => {
                const note = typeof data?.note === 'number' ? data.note : null;
                const weight = typeof data?.weight === 'number' ? data.weight : 0;
                const contribution = typeof data?.contribution === 'number' ? data.contribution : 0;
                const pct = note != null ? Math.round((note / 10) * 100) : 0;
                return (
                  <div key={axe} className="space-y-1" data-testid={`axis-${axe}`}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-zinc-700 font-medium">{AXIS_LABELS[axe] || axe}</span>
                      <span className="text-zinc-500">
                        <strong className="text-zinc-900">{note != null ? note.toFixed(1) : '—'}/10</strong>
                        <span className="ml-2 text-[11px]">poids {weight}%</span>
                        <span className="ml-2 text-[11px]">contrib. {contribution.toFixed(1)}</span>
                      </span>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: themeColor }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Axes non saisis */}
            {score.missing_axes?.length > 0 && (
              <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                ⓘ Axes non encore évalués : {score.missing_axes.map((a) => AXIS_LABELS[a] || a).join(', ')}. Le score est normalisé pour ne pas vous pénaliser.
              </div>
            )}

            {score.comment && (
              <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200 rounded">
                <p className="text-xs font-semibold text-zinc-600 mb-1">Commentaire administration</p>
                <p className="text-sm text-zinc-700">{score.comment}</p>
              </div>
            )}
          </>
        )}
      </CardContent>

      <LexiconModal open={lexOpen} onClose={() => setLexOpen(false)} lexicon={lexicon} profile={profile} />
    </Card>
  );
};

export default MyScoringCard;
